import React, { useState, useEffect } from "react";
import { POS_COLORS } from "../data/players";
import { buildBoardView, buildRosterView } from "./draftHistoryPresenters";
import theme from "../data/theme";

/**
 * Read-only board of a league's stored auction results.
 *
 * Reads league.auctionHistory as captured at import — no fetching, no editing,
 * no derived analysis. This is the receipts view that sits behind the market
 * profile: when a user wants to know why their league "pays up for RBs", this
 * is where they check the actual prices.
 *
 * Two ways to read the same season, because the two questions people bring
 * here are different: "how did the draft go?" (Board — sale order, who bought
 * what) and "what did each team end up with?" (Rosters). Ordering/grouping
 * rules live in draftHistoryPresenters.js and are verified headlessly against
 * a real league.
 *
 * Keeper picks are shown WITH a badge rather than filtered out. They're part
 * of the draft that happened; the profile excludes them from its math, and the
 * badge is what makes that exclusion legible.
 */

const posChipStyle = (pos, size) => {
  const c = POS_COLORS[pos] || POS_COLORS.BN || { bg: theme.colors.bg.elevated, text: theme.colors.text.muted };
  return {
    flexShrink: 0, width: size === "sm" ? 28 : 34, textAlign: "center",
    fontSize: size === "sm" ? 8 : 9, fontWeight: 700, letterSpacing: "0.5px",
    padding: size === "sm" ? "1px 0" : "2px 0", borderRadius: 4,
    background: c.bg, color: c.text,
  };
};

const keeperBadgeStyle = {
  flexShrink: 0, fontSize: 9, fontWeight: 800, letterSpacing: "0.5px",
  padding: "1px 5px", borderRadius: 4, lineHeight: 1.4,
  color: theme.colors.brand.orange,
  background: theme.helpers.brandBg(0.12),
  border: `1px solid ${theme.colors.border.brandLight}`,
};

const KEEPER_TITLE = "Keeper — excluded from this league's market profile";

export default function DraftHistoryModal({ isOpen, onClose, leagueName, history, members }) {
  // Selection is by draftId, not season label — a league can technically run
  // two auctions in one season, and season strings would collide.
  const [draftId, setDraftId] = useState(null);
  const [view, setView] = useState("board");

  useEffect(() => {
    if (!isOpen) return;
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Newest-first is how the history is stored, so "no selection" means newest.
  // The view resets too: Board is the default reading of a draft.
  useEffect(() => { if (isOpen) { setDraftId(null); setView("board"); } }, [isOpen]);

  if (!isOpen || !Array.isArray(history) || history.length === 0) return null;

  const active = history.find(h => h.draftId === draftId) || history[0];
  const board = buildBoardView(active, members);
  const rosters = buildRosterView(active, members);
  const isBoard = view === "board";

  // Rosters needs room for one panel per team; Board is a single list and
  // stays narrow. Same column math as the in-app rosters modal.
  const columns = Math.max(1, Math.ceil(rosters.panels.length / 2));
  const modalMaxWidth = isBoard ? 600 : Math.min(columns * 180 + 80, 1600);

  const tabStyle = isActive => ({
    padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700,
    fontFamily: "inherit", cursor: isActive ? "default" : "pointer",
    background: isActive ? theme.colors.brand.orange : "transparent",
    color: isActive ? theme.colors.bg.app : theme.colors.text.secondary,
    border: `1px solid ${isActive ? theme.colors.brand.orange : theme.colors.border.default}`,
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: modalMaxWidth, width: "95%", maxHeight: "90vh", padding: 20, display: "flex", flexDirection: "column" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexShrink: 0 }}>
          <h3 style={{ fontFamily: "inherit", fontWeight: 700, fontSize: 18, margin: 0, color: theme.colors.brand.orange }}>
            {leagueName} — Draft History
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", color: theme.colors.text.secondary, fontSize: 22, cursor: "pointer", lineHeight: 1, padding: 4, fontFamily: "inherit" }}
          >×</button>
        </div>

        {/* View toggle. A segmented pair rather than more pills, so it reads as
            a different axis from the season tabs directly below it. */}
        <div style={{ display: "flex", marginBottom: 8, flexShrink: 0 }} role="tablist" aria-label="Draft history view">
          {[["board", "Board"], ["rosters", "Rosters"]].map(([id, label], i) => {
            const isActive = view === id;
            return (
              <button
                key={id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setView(id)}
                style={{
                  padding: "5px 16px", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                  cursor: isActive ? "default" : "pointer",
                  background: isActive ? theme.helpers.brandBg(0.15) : "transparent",
                  color: isActive ? theme.colors.brand.orange : theme.colors.text.secondary,
                  border: `1px solid ${isActive ? theme.colors.border.brandLight : theme.colors.border.default}`,
                  borderRadius: i === 0 ? "6px 0 0 6px" : "0 6px 6px 0",
                  marginLeft: i === 0 ? 0 : -1,
                }}
              >{label}</button>
            );
          })}
        </div>

        {/* Season tabs. A single-season league still gets its tab — it labels
            what you're looking at, and a lone tab is cheaper than a heading
            that only appears in one case. */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10, flexShrink: 0 }}>
          {history.map(season => (
            <button
              key={season.draftId}
              onClick={() => setDraftId(season.draftId)}
              style={tabStyle(season.draftId === active.draftId)}
            >{season.season}</button>
          ))}
        </div>

        <div style={{ fontSize: 11, color: theme.colors.text.muted, marginBottom: 8, flexShrink: 0 }}>
          {board.rows.length} pick{board.rows.length !== 1 ? "s" : ""} · ${board.total.toLocaleString()} total
          {!isBoard && rosters.panels.length > 0 && ` · ${rosters.panels.length} teams`}
          {/* Quiet, and only when it's actionable: a history saved before the
              pickNo rider can't be ordered, and re-importing is the only fix. */}
          {isBoard && !board.trueOrder && (
            <span style={{ color: theme.colors.text.faint }}>
              {" "}· ordered by price — re-import for full draft order
            </span>
          )}
        </div>

        {/* The one scroller. flex:1 + minHeight:0 is what lets it shrink inside
            the 90vh modal instead of pushing the header off on short screens. */}
        {isBoard ? (
          <div style={{ flex: 1, overflowY: "auto", minHeight: 0, WebkitOverflowScrolling: "touch" }}>
            {board.rows.map(({ pick, no, owner }, i) => (
              <div
                key={`${pick.name}-${pick.pos}-${i}`}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "5px 6px", borderRadius: 4,
                  background: i % 2 ? "transparent" : theme.colors.bg.deep,
                }}
              >
                {/* Blank rather than a row index when the real pick number is
                    unknown — a number here would read as draft position. */}
                <span style={{
                  flexShrink: 0, width: 26, textAlign: "right",
                  fontSize: 10, fontWeight: 700, color: theme.colors.text.faint,
                  fontVariantNumeric: "tabular-nums",
                }}>{no ?? ""}</span>
                <span style={posChipStyle(pick.pos)}>{pick.pos || "—"}</span>
                <span style={{
                  flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600,
                  color: theme.colors.text.primary,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>{pick.name}</span>
                {pick.keeper && <span title={KEEPER_TITLE} style={keeperBadgeStyle}>K</span>}
                <span
                  className="dh-owner"
                  title={owner}
                  style={{
                    flexShrink: 0, fontSize: 11, fontWeight: 600,
                    color: theme.colors.text.secondary, textAlign: "right",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}
                >{owner}</span>
                <span style={{
                  flexShrink: 0, width: 44, textAlign: "right",
                  fontSize: 12, fontWeight: 700, color: theme.colors.text.secondary,
                }}>${pick.amount}</span>
              </div>
            ))}
          </div>
        ) : rosters.panels.length === 0 ? (
          <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", fontSize: 12, color: theme.colors.text.muted, padding: 20 }}>
            This history was saved before manager data was captured.<br />
            Re-import the league to see rosters.
          </div>
        ) : (
          <div
            className="view-all-rosters-grid draft-history-rosters"
            style={{ "--cols": columns, gap: 8, flex: 1, overflowY: "auto", overflowX: "auto", minHeight: 0 }}
          >
            {rosters.panels.map(panel => (
              <div
                key={panel.key}
                style={{
                  background: theme.colors.bg.surface,
                  border: `1px solid ${theme.colors.border.subtle}`,
                  borderRadius: 8,
                  padding: "8px 10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div>
                  <div
                    title={panel.manager}
                    style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  >{panel.manager}</div>
                  <div style={{ fontSize: 10, color: theme.colors.text.secondary, marginTop: 2 }}>
                    <span style={{ fontWeight: 700, color: theme.colors.brand.accent }}>${panel.total}</span>
                    {` · ${panel.picks.length} pick${panel.picks.length !== 1 ? "s" : ""}`}
                    {panel.keeperCount > 0 && ` · ${panel.keeperCount} K`}
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {panel.picks.map((p, i) => (
                    <div key={`${p.name}-${p.pos}-${i}`} style={{ display: "flex", alignItems: "center", gap: 5, padding: "1px 0" }}>
                      <span style={posChipStyle(p.pos, "sm")}>{p.pos || "—"}</span>
                      <span
                        className="vr-player-name"
                        title={p.name}
                        style={{ flex: 1, minWidth: 0, fontSize: 10, fontWeight: 500, color: theme.colors.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      >{p.name}</span>
                      {p.keeper && (
                        <span title={KEEPER_TITLE} style={{ ...keeperBadgeStyle, fontSize: 8, padding: "0 3px" }}>K</span>
                      )}
                      <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, color: theme.colors.brand.accent }}>${p.amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Only surfaces when it would otherwise look like money went missing:
            picks with no owner data can't be seated in any panel. */}
        {!isBoard && rosters.orphanCount > 0 && rosters.panels.length > 0 && (
          <div style={{ fontSize: 10, color: theme.colors.text.faint, marginTop: 8, flexShrink: 0 }}>
            {rosters.orphanCount} pick{rosters.orphanCount !== 1 ? "s" : ""} (${rosters.orphanTotal}) with no manager on record — re-import to seat them.
          </div>
        )}
      </div>
    </div>
  );
}
